import { useState, useCallback } from 'react';
import {
  startStreamSession,
  StartSessionParams,
  StreamSession,
} from 'src/services/streamingService';
import { useNavigate } from 'react-router-dom';

interface UseStreamStartProps {
  appId: string;
  onSuccess?: (session: StreamSession) => void;
}

/**
 * Hook for starting a new stream session
 * Simple format matching sample project - only needs appId
 */
export function useStreamStart({ appId, onSuccess }: UseStreamStartProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  console.log('=== useStreamStart initialized ===');
  console.log('Props received:', { appId });

  const mutateAsync = useCallback(async () => {
    console.log('=== mutateAsync called ===');
    console.log('Creating session with params:', { appId });

    try {
      setIsLoading(true);
      setIsError(false);
      setError(null);

      const params: StartSessionParams = {
        appId,
      };

      const session = await startStreamSession(params);

      // Navigate to the session if no onSuccess handler provided
      if (onSuccess) {
        onSuccess(session);
      } else {
        navigate(`/app/${appId}/sessions/${session.id}`);
      }

      return session;
    } catch (err) {
      setIsError(true);
      setError(
        err instanceof Error ? err : new Error('Failed to start stream session')
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [appId, navigate, onSuccess]);

  return {
    mutateAsync,
    isLoading,
    isError,
    error,
    isPending: isLoading,
  };
}
